import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

type Line = { id: number; code: string; name: string; supplierName: string | null };
type SupplierGroup = { id: number; name: string; lines: { id: number; code: string }[] };
type Rep = {
  id: number;
  username: string;
  fullName: string;
  email: string | null;
  role: 'REP' | 'REP_SUPER' | 'ADMIN' | 'COMPTOIR' | 'REP_JEUX';
  isActive: boolean;
  lastLoginAt: string | null;
  lines: Line[];
  supplierIds: number[];
};

export default function Reps() {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    username: '',
    fullName: '',
    password: '',
    role: 'REP' as 'REP' | 'REP_SUPER' | 'ADMIN' | 'COMPTOIR' | 'REP_JEUX',
    lineIds: [] as number[],
  });
  const [resetting, setResetting] = useState<number | null>(null);
  const [newPassword, setNewPassword] = useState('');

  const reps = useQuery<{ reps: Rep[] }>({
    queryKey: ['admin', 'reps'],
    queryFn: () => api('/api/admin/reps'),
  });
  const lines = useQuery<{ lines: Line[] }>({
    queryKey: ['admin', 'lines'],
    queryFn: () => api('/api/admin/lines'),
  });
  // Fournisseurs (= groupes) avec leurs lignes : un rep est rattaché à UN
  // fournisseur, ses lignes découlent de celui-ci (définies dans la page Lignes).
  const suppliersQ = useQuery<{ groups: SupplierGroup[] }>({
    queryKey: ['admin', 'rewards'],
    queryFn: () => api('/api/admin/rewards'),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['admin', 'reps'] });

  const create = useMutation({
    mutationFn: () =>
      api('/api/admin/reps', {
        method: 'POST',
        body: JSON.stringify({
          ...form,
          supplierIds: form.role === 'REP' ? newRepSuppliers : [],
        }),
      }),
    onSuccess: () => {
      setForm({ username: '', fullName: '', password: '', role: 'REP', lineIds: [] });
      setNewRepSuppliers([]);
      void invalidate();
    },
  });

  const toggle = useMutation({
    mutationFn: ({ id, isActive }: { id: number; isActive: boolean }) =>
      api(`/api/admin/reps/${id}`, { method: 'PATCH', body: JSON.stringify({ isActive }) }),
    onSuccess: invalidate,
  });

  const resetPassword = useMutation({
    mutationFn: ({ id, password }: { id: number; password: string }) =>
      api(`/api/admin/reps/${id}/password`, { method: 'POST', body: JSON.stringify({ password }) }),
    onSuccess: () => {
      setResetting(null);
      setNewPassword('');
    },
  });

  // Édition d'un rep (nom, courriel, rôle). Le nom d'utilisateur reste immuable.
  const [editing, setEditing] = useState<number | null>(null);
  const [editForm, setEditForm] = useState({ fullName: '', email: '', role: 'REP' as Rep['role'] });
  const update = useMutation({
    mutationFn: ({
      id,
      ...body
    }: {
      id: number;
      fullName: string;
      email: string | null;
      role: Rep['role'];
      supplierIds?: number[];
    }) => api(`/api/admin/reps/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    onSuccess: () => {
      setEditing(null);
      void invalidate();
    },
  });
  function openEdit(r: Rep) {
    setEditing(r.id);
    setEditForm({ fullName: r.fullName, email: r.email ?? '', role: r.role });
    setEditSuppliers(r.supplierIds ?? []);
    setResetting(null);
  }

  const allLines = lines.data?.lines ?? [];

  // Fournisseurs choisis pour un nouveau rep, et pour le rep en cours d'édition
  // (un rep peut en avoir plusieurs).
  const [newRepSuppliers, setNewRepSuppliers] = useState<number[]>([]);
  const [editSuppliers, setEditSuppliers] = useState<number[]>([]);

  const supplierGroups = suppliersQ.data?.groups ?? [];
  const supplierName = (id: number) => supplierGroups.find((g) => g.id === id)?.name ?? `#${id}`;
  const toggleIn = (arr: number[], id: number) =>
    arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id];

  // Tri de la liste des représentants (côté écran).
  const [sortBy, setSortBy] = useState<'username' | 'fullName' | 'role' | 'lastLogin' | 'active'>(
    'role',
  );
  const roleRank: Record<Rep['role'], number> = {
    ADMIN: 0,
    REP_JEUX: 1,
    REP_SUPER: 2,
    COMPTOIR: 3,
    REP: 4,
  };
  const repsTries = [...(reps.data?.reps ?? [])].sort((a, b) => {
    switch (sortBy) {
      case 'fullName':
        return a.fullName.localeCompare(b.fullName);
      case 'role':
        return roleRank[a.role] - roleRank[b.role] || a.fullName.localeCompare(b.fullName);
      case 'lastLogin': {
        const ta = a.lastLoginAt ? Date.parse(a.lastLoginAt) : 0;
        const tb = b.lastLoginAt ? Date.parse(b.lastLoginAt) : 0;
        return tb - ta; // plus récent d'abord
      }
      case 'active':
        return Number(b.isActive) - Number(a.isActive) || a.fullName.localeCompare(b.fullName);
      default:
        return a.username.localeCompare(b.username);
    }
  });

  return (
    <div className="space-y-6">
      <section className="bg-card rounded-xl border p-6">
        <h2 className="mb-4 text-lg font-semibold">Nouveau représentant</h2>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            create.mutate();
          }}
          className="space-y-4"
        >
          <div className="flex flex-wrap gap-3">
            <label className="space-y-1.5">
              <span className="block text-sm font-medium">Nom d'utilisateur ou courriel</span>
              <input
                value={form.username}
                onChange={(e) => setForm({ ...form, username: e.target.value.toLowerCase() })}
                placeholder="nom.utilisateur ou nom@domaine.com"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                className="border-input bg-background h-11 w-72 rounded-lg border px-3 font-mono"
                required
              />
            </label>
            <label className="space-y-1.5">
              <span className="block text-sm font-medium">Nom complet</span>
              <input
                value={form.fullName}
                onChange={(e) => setForm({ ...form, fullName: e.target.value })}
                className="border-input bg-background h-11 w-64 rounded-lg border px-3"
                required
              />
            </label>
            <label className="space-y-1.5">
              <span className="block text-sm font-medium">Mot de passe (8 min.)</span>
              <input
                type="text"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="border-input bg-background h-11 w-48 rounded-lg border px-3 font-mono"
                required
              />
            </label>
            <label className="space-y-1.5">
              <span className="block text-sm font-medium">Rôle</span>
              <select
                value={form.role}
                onChange={(e) =>
                  setForm({
                    ...form,
                    role: e.target.value as 'REP' | 'REP_SUPER' | 'ADMIN' | 'COMPTOIR' | 'REP_JEUX',
                    lineIds: [],
                  })
                }
                className="border-input bg-background h-11 rounded-lg border px-3"
              >
                <option value="REP">Représentant</option>
                <option value="REP_SUPER">Représentant Super (toutes lignes)</option>
                <option value="REP_JEUX">Compte Jeux</option>
                <option value="COMPTOIR">Comptoir (cadeaux)</option>
                <option value="ADMIN">Administrateur</option>
              </select>
            </label>
          </div>

          {/* Un représentant est rattaché à un ou plusieurs fournisseurs ; ses lignes
              en découlent (dynamiquement, selon la page Lignes). */}
          {form.role === 'REP' && (
            <fieldset>
              <div className="mb-2 flex items-center gap-3">
                <legend className="text-sm font-medium">Fournisseur(s)</legend>
                {supplierGroups.length > 0 && (
                  <button
                    type="button"
                    onClick={() =>
                      setNewRepSuppliers(
                        newRepSuppliers.length === supplierGroups.length
                          ? []
                          : supplierGroups.map((g) => g.id),
                      )
                    }
                    className="text-primary text-sm font-medium underline underline-offset-4"
                  >
                    {newRepSuppliers.length === supplierGroups.length
                      ? 'Tout désélectionner'
                      : 'Tout sélectionner'}
                  </button>
                )}
              </div>
              {supplierGroups.length === 0 ? (
                <p className="text-muted-foreground text-sm">
                  Aucun fournisseur. Créez-en un dans la section <b>Lignes</b>.
                </p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {supplierGroups.map((g) => {
                    const checked = newRepSuppliers.includes(g.id);
                    return (
                      <label
                        key={g.id}
                        className={`cursor-pointer rounded-lg border px-3 py-2 text-sm ${
                          checked ? 'border-primary bg-primary/10 font-medium' : 'border-input'
                        }`}
                      >
                        <input
                          type="checkbox"
                          className="sr-only"
                          checked={checked}
                          onChange={() => setNewRepSuppliers(toggleIn(newRepSuppliers, g.id))}
                        />
                        {g.name}
                        <span className="text-muted-foreground">
                          {' '}
                          ({g.lines.map((l) => l.code).join(', ') || 'aucune ligne'})
                        </span>
                      </label>
                    );
                  })}
                </div>
              )}
            </fieldset>
          )}

          {(form.role === 'REP_SUPER' || form.role === 'REP_JEUX') && (
            <p className="text-muted-foreground text-sm">
              Accès à <b>toutes les lignes</b> + consultation cadeaux et commandes des clients —
              aucune assignation de ligne nécessaire.
              {form.role === 'REP_JEUX' && (
                <> Dédié au concours : <b>conservé lors de la réinitialisation</b> du show.</>
              )}
            </p>
          )}

          <button
            type="submit"
            disabled={create.isPending}
            className="bg-primary text-primary-foreground h-11 rounded-lg px-5 font-medium disabled:opacity-50"
          >
            Créer
          </button>
          {create.isError && (
            <p className="text-destructive text-sm font-medium">{(create.error as Error).message}</p>
          )}
        </form>
      </section>

      <section className="bg-card space-y-4 rounded-xl border p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-lg font-semibold">Représentants ({repsTries.length})</h2>
          <label className="text-muted-foreground flex items-center gap-1.5 text-sm">
            Trier par
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
              className="border-input bg-background h-10 rounded-lg border px-2 text-sm"
            >
              <option value="role">Rôle</option>
              <option value="fullName">Nom complet</option>
              <option value="username">Nom d'utilisateur</option>
              <option value="lastLogin">Dernière connexion</option>
              <option value="active">État (actifs d'abord)</option>
            </select>
          </label>
        </div>

        {repsTries.map((r) => (
          <div key={r.id} className="space-y-3 rounded-lg border p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="font-medium">
                  {r.fullName}{' '}
                  <span className="text-muted-foreground font-mono text-sm">({r.username})</span>
                  {r.role === 'REP' && (
                    <span className="bg-primary/10 text-primary ml-2 rounded px-2 py-0.5 text-xs font-medium">
                      représentant
                    </span>
                  )}
                  {r.role === 'REP_SUPER' && (
                    <span className="ml-2 rounded bg-indigo-500/15 px-2 py-0.5 text-xs font-medium text-indigo-700 dark:text-indigo-400">
                      rep super
                    </span>
                  )}
                  {r.role === 'REP_JEUX' && (
                    <span className="ml-2 rounded bg-emerald-500/15 px-2 py-0.5 text-xs font-medium text-emerald-700 dark:text-emerald-400">
                      compte jeux
                    </span>
                  )}
                  {r.role === 'ADMIN' && (
                    <span className="bg-secondary ml-2 rounded px-2 py-0.5 text-xs font-medium">admin</span>
                  )}
                  {r.role === 'COMPTOIR' && (
                    <span className="ml-2 rounded bg-amber-500/15 px-2 py-0.5 text-xs font-medium text-amber-700 dark:text-amber-400">
                      comptoir
                    </span>
                  )}
                  {!r.isActive && <span className="text-destructive ml-2 text-xs">désactivé</span>}
                </p>
                <p className="text-muted-foreground text-xs">
                  Dernière connexion :{' '}
                  {r.lastLoginAt ? new Date(r.lastLoginAt).toLocaleString('fr-CA') : 'jamais'}
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => (editing === r.id ? setEditing(null) : openEdit(r))}
                  className="border-input rounded-lg border px-3 py-1.5 text-sm"
                >
                  Modifier
                </button>
                <button
                  onClick={() => setResetting(resetting === r.id ? null : r.id)}
                  className="border-input rounded-lg border px-3 py-1.5 text-sm"
                >
                  Mot de passe
                </button>
                <button
                  onClick={() => toggle.mutate({ id: r.id, isActive: !r.isActive })}
                  className="border-input rounded-lg border px-3 py-1.5 text-sm"
                >
                  {r.isActive ? 'Désactiver' : 'Réactiver'}
                </button>
              </div>
            </div>

            {editing === r.id && (
              <div className="bg-muted/40 space-y-3 rounded-lg border p-3">
                <div className="flex flex-wrap gap-3">
                  <label className="space-y-1">
                    <span className="block text-xs font-medium">Nom complet</span>
                    <input
                      value={editForm.fullName}
                      onChange={(e) => setEditForm({ ...editForm, fullName: e.target.value })}
                      className="border-input bg-background h-10 w-64 rounded-lg border px-3 text-sm"
                    />
                  </label>
                  <label className="space-y-1">
                    <span className="block text-xs font-medium">Courriel</span>
                    <input
                      type="email"
                      value={editForm.email}
                      onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                      className="border-input bg-background h-10 w-64 rounded-lg border px-3 text-sm"
                    />
                  </label>
                  <label className="space-y-1">
                    <span className="block text-xs font-medium">Rôle</span>
                    <select
                      value={editForm.role}
                      onChange={(e) => setEditForm({ ...editForm, role: e.target.value as Rep['role'] })}
                      className="border-input bg-background h-10 rounded-lg border px-3 text-sm"
                    >
                      <option value="REP">Représentant</option>
                      <option value="REP_SUPER">Représentant Super (toutes lignes)</option>
                      <option value="REP_JEUX">Compte Jeux</option>
                      <option value="COMPTOIR">Comptoir (cadeaux)</option>
                      <option value="ADMIN">Administrateur</option>
                    </select>
                  </label>
                </div>
                {editForm.role === 'REP' && supplierGroups.length > 0 && (
                  <fieldset>
                    <div className="mb-2 flex items-center gap-3">
                      <legend className="text-xs font-medium">Fournisseur(s)</legend>
                      <button
                        type="button"
                        onClick={() =>
                          setEditSuppliers(
                            editSuppliers.length === supplierGroups.length
                              ? []
                              : supplierGroups.map((g) => g.id),
                          )
                        }
                        className="text-primary text-xs font-medium underline underline-offset-4"
                      >
                        {editSuppliers.length === supplierGroups.length
                          ? 'Tout désélectionner'
                          : 'Tout sélectionner'}
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {supplierGroups.map((g) => {
                        const checked = editSuppliers.includes(g.id);
                        return (
                          <label
                            key={g.id}
                            className={`cursor-pointer rounded-lg border px-3 py-1.5 text-sm ${
                              checked ? 'border-primary bg-primary/10 font-medium' : 'border-input'
                            }`}
                          >
                            <input
                              type="checkbox"
                              className="sr-only"
                              checked={checked}
                              onChange={() => setEditSuppliers(toggleIn(editSuppliers, g.id))}
                            />
                            {g.name}
                          </label>
                        );
                      })}
                    </div>
                  </fieldset>
                )}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() =>
                      update.mutate({
                        id: r.id,
                        fullName: editForm.fullName.trim(),
                        email: editForm.email.trim() || null,
                        role: editForm.role,
                        // Un rep : ses fournisseurs (ses lignes en découlent, dynamiquement).
                        ...(editForm.role === 'REP' ? { supplierIds: editSuppliers } : {}),
                      })
                    }
                    disabled={update.isPending || !editForm.fullName.trim()}
                    className="bg-primary text-primary-foreground h-10 rounded-lg px-4 text-sm font-medium disabled:opacity-40"
                  >
                    {update.isPending ? 'Enregistrement…' : 'Enregistrer'}
                  </button>
                  <button
                    onClick={() => setEditing(null)}
                    className="border-input h-10 rounded-lg border px-4 text-sm font-medium"
                  >
                    Annuler
                  </button>
                  {update.isError && (
                    <p className="text-destructive text-sm font-medium">
                      {(update.error as Error).message}
                    </p>
                  )}
                </div>
                {(editForm.role === 'REP_SUPER' || editForm.role === 'REP_JEUX') && (
                  <p className="text-muted-foreground text-xs">
                    {editForm.role === 'REP_JEUX' ? 'Compte Jeux' : 'Rep super'} : accès à toutes les
                    lignes + consultation cadeaux et commandes
                    {editForm.role === 'REP_JEUX' && ' — conservé lors de la réinitialisation'}.
                  </p>
                )}
              </div>
            )}

            {resetting === r.id && (
              <div className="flex flex-wrap items-end gap-2">
                <input
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Nouveau mot de passe"
                  className="border-input bg-background h-10 w-56 rounded-lg border px-3 font-mono text-sm"
                />
                <button
                  onClick={() => resetPassword.mutate({ id: r.id, password: newPassword })}
                  disabled={newPassword.length < 8 || resetPassword.isPending}
                  className="bg-primary text-primary-foreground h-10 rounded-lg px-4 text-sm font-medium disabled:opacity-40"
                >
                  Appliquer
                </button>
                {resetPassword.isError && (
                  <p className="text-destructive text-sm">{(resetPassword.error as Error).message}</p>
                )}
              </div>
            )}

            {/* Fournisseur(s) rattachés (lecture seule ; modifiable via « Modifier »). */}
            {r.role === 'REP' && (
              <p className="text-sm">
                {r.supplierIds.length > 0 ? (
                  <>
                    <span className="text-muted-foreground">
                      Fournisseur{r.supplierIds.length > 1 ? 's' : ''} :{' '}
                    </span>
                    <span className="font-medium">
                      {r.supplierIds.map((id) => supplierName(id)).join(', ')}
                    </span>
                    {r.lines.length > 0 && (
                      <span className="text-muted-foreground">
                        {' '}
                        — {r.lines.map((l) => l.code).join(', ')}
                      </span>
                    )}
                  </>
                ) : (
                  <span className="text-destructive font-medium">Aucun fournisseur assigné.</span>
                )}
              </p>
            )}
          </div>
        ))}
      </section>
    </div>
  );
}
